import { CommonModule } from "@angular/common";
import { afterNextRender, Component, ElementRef, inject, Injector, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from "@angular/router";
import saveAs from "file-saver";
import _ from "lodash";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { DrawerModule } from "primeng/drawer";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputNumberModule } from "primeng/inputnumber";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SelectModule } from "primeng/select";
import { Table, TableModule } from "primeng/table";
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { DialogComponent } from "../../../../../@core/components/ui/dialog/dialog.component";
import { MultiSelectComponent } from "../../../../../@core/components/ui/multi-select";
import { PopoverComponent } from "../../../../../@core/components/ui/popover/popover.component";
import { SelectComponent, SelectConfig, SelectOption } from "../../../../../@core/components/ui/select";
import { DEACTIVATION_STATUS, IAttachment, IUser, ResponseJson } from "../../../../../@core/models/model";
import { AuthenticationService } from "../../../../../@core/services/authentication.service";
import { DialogService } from "../../../../../@core/services/dialog.service";
import { CLIENT_EVENTS, SOCKET_EVENTS, SocketService } from "../../../../../@core/services/socket.service";
import { ToastService } from "../../../../../@core/services/toast.service";
import { DataTableComponent } from "../../../../../@shared/components/data-table/data-table.component";
import { FileDropComponent } from "../../../../../@shared/components/file-drop/file-drop.component";
import { MultiFormatPreviewComponent } from "../../../../../@shared/components/multi-format-preview/multi-format-preview.component";
import { FileDropTypes } from "../../../../../@shared/components/file-drop/file-drop.types";
import { SearchBarComponent } from "../../../../../@shared/components/search-bar/search-bar.component";
import { UserNameDisplayComponent } from "../../../../../@shared/components/user-name-display";
import { DynamicComponentRenderer } from "../../../../../@shared/directives/dynamic-component-renderer.directive";
import { SORT_ORDER, TableColumn, TableConfig } from "../../../../../@shared/models/table.models";
import { AssetUrlPipe } from "../../../../../@shared/pipes/asset-url.pipe";
import { FormatTextPipe } from "../../../../../@shared/pipes/formated-text.pipe";
import { FORM_QUESTION_STATUS, IFormAnswer, IFormQuestion } from "../../../../models/contract-management.model";
import { IQnaPairMetadata, QNA_PAIR_METADATA_STATUS } from "../../../../models/settings.model";
import { DocumentTypeFilter, DocumentTypeService } from "../../../../services/document-type.service";
import { EXPORT_TYPE, IQnaPair, IQnaPairResponse, QnAPairService } from "../../../../services/qna-pair.service";
import { UserService } from "../../../../services/user.service";
import { QNA_PAIR_TABLE_COLUMNS, QNA_PAIR_TABLE_CONFIG } from './qna-pair-table.config';
import { CreditsOverviewComponent } from '../../../../../features/credits/components/credits-overview/credits-overview.component';
import { UserCreditsStateService } from '../../../../../features/credits/services/user-credits-state.service';
import { UserCreditsService } from '../../../../../features/credits/services/user-credits.service';

@Component({
    selector: 'app-qna-pair',
    imports: [
        InputGroupModule,
        InputGroupAddonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        SelectModule,
        ToastModule,
        FormatTextPipe,
        DynamicComponentRenderer,
        DrawerModule,
        InputNumberModule,
        ProgressSpinnerModule,
        CommonModule,
        CardModule,
        InfiniteScrollDirective,
        CheckboxModule,
        FileDropComponent,
        SearchBarComponent,
        DataTableComponent,
        ButtonComponent,
        SelectComponent,
        PopoverComponent,
        MultiSelectComponent,
        RouterModule,
        AssetUrlPipe,
        TooltipModule,
        DialogComponent,
        DeleteConfirmationComponent,
        UserNameDisplayComponent,
        CreditsOverviewComponent,
        MultiFormatPreviewComponent,
    ],
    standalone: true,
    templateUrl: './qna-pair.component.html',
    styleUrl: './qna-pair.component.scss'
})
export class QnAPairComponent implements OnInit, OnDestroy {
    organization: string = '';
    private routeSubscription?: Subscription;
    // Main table properties
    searchValue: string = '';
    qnaLoading: boolean = false;
    documentTypeId: string = '';
    qnaData: (IQnaPair & { reviewers: Partial<IUser>[], questionTitle?: string, lastEdited?: string })[] = [];
    skip: number = 0;
    limit: number = 0;
    documentTypeOptions: SelectOption[] = [];
    documentTypeConfig: SelectConfig = {
        placeholder: 'Select document type',
        searchable: true,
        clearable: false,
        appendTo: 'body',
        styleClass: 'font-semibold rounded-xl text-black border border-gray-200 w-full',
        panelStyleClass: 'w-[10rem]'
    };
    selectedPairs: IQnaPair[] = [];
    selectedPairsIds: string[] = [];
    selectAllChecked: boolean = false;
    @ViewChild('dt') table!: Table;

    // Template References for data table
    @ViewChild('questionTemplateRef') questionTemplateRef!: TemplateRef<any>;
    @ViewChild('answerTemplateRef') answerTemplateRef!: TemplateRef<any>;
    @ViewChild('authorTemplateRef') authorTemplateRef!: TemplateRef<any>;
    @ViewChild('reviewedByTemplateRef') reviewedByTemplateRef!: TemplateRef<any>;
    @ViewChild('documentTemplateRef') documentTemplateRef!: TemplateRef<any>;
    @ViewChild('sourceTypeTemplateRef') sourceTypeTemplateRef!: TemplateRef<any>;
    @ViewChild('lastEditedTemplateRef') lastEditedTemplateRef!: TemplateRef<any>;
    @ViewChild('dataTable') dataTable!: DataTableComponent;
    @ViewChildren('qnaCard') qnaCards!: QueryList<ElementRef>;

    // Table Configuration
    qnaTableColumns: TableColumn[] = [...QNA_PAIR_TABLE_COLUMNS];
    qnaTableConfig: TableConfig = {
        ...QNA_PAIR_TABLE_CONFIG,
        columnSortingFunction: (field: string, order: SORT_ORDER) => this.sortQnaPairs(field, order)
    };

    // Edit Dialog Config & properties
    showBulkEditDialog: boolean = false;
    editingPairs: (IQnaPair & { question?: string, answer?: string, reviewer?: string })[] = [];
    originalPairs: (IQnaPair & { question?: string, answer?: string, reviewer?: string })[] = [];
    currentEditIndex: number = 0;
    savedPairs: Set<number> = new Set(); // index of pairs 


    //single edit dialog
    isEditing: boolean = false;

    private readonly injector = inject(Injector);
    creditsState = inject(UserCreditsStateService);
    private userCreditsService = inject(UserCreditsService);

    // Enums
    DEACTIVATION_STATUS = DEACTIVATION_STATUS;
    selectedPair: IQnaPair & { question?: string, answer?: string, reviewer?: string } | null = null;


    // Edit Dialog Variables
    totalEditingCount: number = 0;
    isPreviousDisabled: boolean = true;
    isNextDisabled: boolean = true;
    isSaveDisabled: boolean = true;
    isLoadingMorePairs: boolean = false;
    isSaving: boolean = false;
    editDialogLimit: number = 10;
    editDialogSkip: number = 0;


    admins: IUser[] = [];
    userOptions: { label: string; value: string }[] = [];
    isLoadingUsers: boolean = false;
    expandCell: boolean = false;
    expandedCellContent: string = '';
    expandedCellType: string = '';
    filterDialog: boolean = false;
    authorOptions: { name: string, value: string }[] = [];
    selectedAuthors: string[] = [];
    reviewerOptions: { name: string, value: string }[] = [];
    selectedReviewers: string[] = [];
    minTimeUsed: number | null = null;
    maxTimeUsed: number | null = null;
    users: IUser[] = [];
    totalRecords: number = 0;
    showManualQnaDialog: boolean = false;
    saveManualQnaLoading: boolean = false;
    questionText: string = ""
    answerText: string = ""


    // download dialog
    showDownloadDialog: boolean = false;
    exportType: EXPORT_TYPE | undefined;
    fileOptions: SelectOption[] = [
        { label: 'Excel', value: EXPORT_TYPE.EXCEL, icon: 'pi pi-file-excel' },
        { label: 'CSV', value: EXPORT_TYPE.CSV, icon: 'pi pi-file-csv' }
    ];
    fileSelectConfig: SelectConfig = {
        placeholder: 'Select a file format',
        searchable: false,
        clearable: true,
        styleClass: 'w-full',
        appendTo: 'body'
    };
    isDownloading: boolean = false;

    // delete confirmation dialog
    showDeleteConfirmDialog: boolean = false;
    itemsToDeleteCount: number = 0;

    // upload dialog
    showUploadDialog: boolean = false;
    selectedFiles: File[] = [];
    public allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC];
    uploadedFilesAudit: { [key: string]: IQnaPairMetadata[] } = {};
    openFilesAuditSidebar: boolean = false;
    showQnaDocumentPreview: boolean = false;
    selectedQnaDocumentFile: IAttachment | null = null;
    selectedQnaDocumentId: string | null = null;
    isProcessingUpload: boolean = false;
    QNA_PAIR_METADATA_STATUS = QNA_PAIR_METADATA_STATUS;

    @ViewChild('mobileMenu') mobileMenu!: any;
    @ViewChild('mobileMenuSmall') mobileMenuSmall!: any;

    closeAllMenus() {
        this.mobileMenu?.hide();
        this.mobileMenuSmall?.hide();
    }

    constructor(
        private qnaPairService: QnAPairService,
        private documentTypeService: DocumentTypeService,
        private toast: ToastService,
        private userService: UserService,
        private dialogService: DialogService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthenticationService,
        private socketService: SocketService,
    ) {
        this.authService.currentUser.subscribe(res => {
            this.organization = res?.organization as string;
        });
        this.route.queryParamMap.subscribe(params => {
            this.emitSocketEvent(CLIENT_EVENTS.JOIN_ORG_ROOM)
        });
        this.socketService.on(SOCKET_EVENTS.QNA_PAIR_EXTRACTED).subscribe((data) => {
            const event = data[0] as { organization: string, documentType: string, qnaPairMetadata: string, status: QNA_PAIR_METADATA_STATUS };
            if (event.organization === this.organization) {
                if (event.status === QNA_PAIR_METADATA_STATUS.FAILED) {
                    this.toast.error('One or more files appear to be corrupted. Please re-upload valid files');
                }
                delete this.uploadedFilesAudit[this.documentTypeId];
                this.getUploadedFilesAudit();
            }
        })
    }

    async emitSocketEvent(event: CLIENT_EVENTS) {
        if (this.organization) {
            const socket = await this.socketService.getSocket()
            if (!socket) {
                console.warn('Socket is not initialized yet:', event)
                return
            }
            socket.emit(event as CLIENT_EVENTS.JOIN_ORG_ROOM | CLIENT_EVENTS.LEAVE_ORG_ROOM, this.organization)
        }
    }

    ngOnInit() {
        this.initialize();
        // Populate the credits signal so app-credits-overview can determine
        // depleted vs ready state for exhausted file extractions.
        this.creditsState.fetchCredits().subscribe();
        let hadChildRoute = this.route.children.length > 0;
        this.routeSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const hasChildRoute = this.route.children.length > 0;
                if (hadChildRoute && !hasChildRoute) {
                    this.initialize();
                }
                hadChildRoute = hasChildRoute;
            });
    }

    initialize() {
        this.getDocTypesWithForm();
        this.getAdmins();
    }

    ngOnDestroy() {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
        this.emitSocketEvent(CLIENT_EVENTS.LEAVE_ORG_ROOM)
    }

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'questionTemplate': this.questionTemplateRef,
            'answerTemplate': this.answerTemplateRef,
            'authorTemplate': this.authorTemplateRef,
            'reviewersTemplate': this.reviewedByTemplateRef,
            'documentTemplate': this.documentTemplateRef,
            'sourceTypeTemplate': this.sourceTypeTemplateRef,
            'lastEditedTemplate': this.lastEditedTemplateRef
        };
    }


    getDocTypesWithForm() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.documentTypeOptions = res.data.map((doc) => {
                    return {
                        label: doc.name,
                        value: doc._id
                    }
                });
                this.documentTypeId = this.documentTypeOptions?.[0]?.value ?? '';
                this.getQnAPairs();
            }
        })
    }

    getQnAPairs(limit: number = this.limit, append: boolean = false) {
        this.qnaLoading = true;
        this.qnaTableConfig = { ...this.qnaTableConfig, loading: true };

        const data = {
            documentTypeId: this.documentTypeId,
            searchValue: this.searchValue,
            skip: this.skip,
            limit: limit,
            filter: {
                author: this.selectedAuthors,
                reviewer: this.selectedReviewers,
                minTimeUsed: this.minTimeUsed,
                maxTimeUsed: this.maxTimeUsed,
                status: [FORM_QUESTION_STATUS.APPROVED]
            }
        }
        this.qnaPairService.getQnAPairs(data).subscribe((res: ResponseJson<IQnaPairResponse>) => {
            this.qnaLoading = false;
            this.qnaTableConfig = { ...this.qnaTableConfig, loading: false };

            if (res.success) {
                if (append) {
                    this.qnaData = [...this.qnaData, ...res.data.data];
                } else {
                    if (res.data.data && res.data.data.length > 0 || this.skip === 0) {
                        this.qnaData = res.data.data;
                    }
                }
                this.totalRecords = res.data.totalRecords;
            }
        });
    }

    onPageChange(event: any) {
        this.skip = event.first;
        this.limit = event.rows;
        this.getQnAPairs(this.limit, false);
    }

    getRowClass(pair: IQnaPair) {
        return {
            'selected-row': this.selectedPairsIds.includes(pair._id)
        };
    }

    onSelectionChange(event: any) {
        this.selectedPairs = event;
        this.selectedPairsIds = event.map((item: IQnaPair) => item._id);
    }

    clearSelections() {
        this.selectedPairs = [];
        this.selectedPairsIds = [];
        this.selectAllChecked = false;
        this.dataTable.clearSelection();
    }

    onBulkEdit() {
        if (this.selectedPairsIds.length === 0) {
            this.toast.error("Please select at least one QnA pair to edit");
            return;
        }
        if (this.selectedPairsIds.length === 1) {
            this.editQna();
            return;
        }
        const data = {
            documentTypeId: this.documentTypeId,
            searchValue: this.searchValue,
            skip: this.editDialogSkip,
            limit: this.selectedPairsIds.length,
            ids: this.selectedPairsIds,
            filter: {
                author: [],
                reviewer: [],
                minTimeUsed: null,
                maxTimeUsed: null
            }
        }
        this.qnaPairService.getQnAPairs(data).subscribe((res) => {
            if (res.success) {
                this.editingPairs = res.data.data.map(pair => ({
                    ...pair,
                    question: (pair.question as IFormQuestion).title,
                    answer: (pair.answer as IFormAnswer).value,
                    reviewer: pair.reviewers.length > 0 ? pair.reviewers[0]._id : undefined
                }));
                this.originalPairs = this.editingPairs.map(pair => ({ ...pair }));
                this.currentEditIndex = 0;
                this.showBulkEditDialog = true;
                this.totalEditingCount = this.editingPairs.length;
                this.isPreviousDisabled = true;
                this.isNextDisabled = this.editingPairs.length < 1;
            } else {
                this.toast.error("Failed to load QnA pairs");
            }
        });
    }

    onBulkDelete() {
        if (this.selectedPairsIds.length === 0) {
            this.toast.error("Please select at least one QnA pair to delete");
            return;
        }
        this.itemsToDeleteCount = this.selectedPairsIds.length;
        this.showDeleteConfirmDialog = true;
    }

    confirmDelete() {
        this.showDeleteConfirmDialog = false;
        this.qnaPairService.deleteQnaPairs(this.selectedPairsIds).subscribe((res) => {
            if (res.success) {
                this.toast.success(`Q&A pair${this.itemsToDeleteCount > 1 ? 's' : ''} deleted successfully`);
                this.clearSelections();
                this.skip = 0;
                this.getQnAPairs();
            } else {
                this.toast.error("Failed to delete Q&A pairs");
                this.clearSelections();
            }
        });
    }

    cancelDelete() {
        this.showDeleteConfirmDialog = false;
        this.itemsToDeleteCount = 0;
    }

    searchQna = _.debounce(() => {
        this.skip = 0;
        this.getQnAPairs();
    }, 300);

    onSearch() {
        this.searchQna();
    }

    handleChange(event: { value: string }) {
        this.documentTypeId = event.value;
        this.skip = 0;
        this.getQnAPairs();
    }

    openFilterDialog() {
        this.filterDialog = true;
    }

    openModel(answer: string, type: string) {
        this.expandCell = !this.expandCell;
        this.expandedCellContent = answer;
        this.expandedCellType = type;
        if (!this.expandCell) {
            this.expandedCellContent = '';
        }
    }


    applyFilter() {
        this.filterDialog = false;
        this.skip = 0;
        this.getQnAPairs();
    }

    clearFilter() {
        this.selectedAuthors = [];
        this.selectedReviewers = [];
        this.minTimeUsed = null;
        this.maxTimeUsed = null;
        this.filterDialog = false;
        this.skip = 0;
        this.getQnAPairs();
    }

    addManualQna() {
        this.selectedPair = null;
        this.showManualQnaDialog = true;
        this.isEditing = false;
    }

    saveQnaData() {
        if (this.selectedPair && this.isEditing) {
            this.saveManualQnaLoading = true;
            this.qnaPairService.updateQnaPair(this.selectedPair._id, this.selectedPair.question, this.selectedPair.answer, this.selectedPair.reviewer || '').subscribe((res) => {
                if (res.success) {
                    this.toast.success("Q&A pair updated successfully");
                    this.showManualQnaDialog = false;
                    this.skip = 0;
                    this.getQnAPairs();
                    this.clearSelections();
                }
                this.saveManualQnaLoading = false;
                this.selectedPair = null;
            });
        } else {
            this.addNewManualQnaData();
        }
    }

    checkValidation() {
        if (this.isEditing && this.selectedPair) {
            return this.selectedPair.question && this.selectedPair.answer;
        }
        return this.questionText && this.answerText;
    }

    addNewManualQnaData() {
        this.saveManualQnaLoading = true;
        this.qnaPairService.addManualQnAPair(this.questionText, this.answerText, this.documentTypeId).subscribe((res) => {
            if (res.success) {
                this.toast.success("Added Q&A pair successfully");
                this.showManualQnaDialog = false;
                this.skip = 0;
                this.getQnAPairs();
                this.questionText = '';
                this.answerText = '';
            }
            this.saveManualQnaLoading = false;
        });
    }

    editQna() {
        const qna = this.qnaData.find(pair => pair._id === this.selectedPairsIds[0]);
        if (qna) {
            this.selectedPair = {
                ...qna,
                question: (qna.question as IFormQuestion).title,
                answer: (qna.answer as IFormAnswer).value,
                reviewer: qna.reviewers.length > 0 ? qna.reviewers[0]._id : undefined
            }
            this.isEditing = true;
            this.showManualQnaDialog = true;
        }
    }


    onDownload() {
        if (this.selectedPairsIds.length === 0) {
            this.toast.error("Please select at least one QnA pair to download");
            return;
        }
        if (!this.exportType || this.isDownloading) {
            return;
        }
        this.isDownloading = true;
        this.qnaPairService.downloadQnaPairs(this.selectedPairsIds, this.exportType ?? EXPORT_TYPE.EXCEL).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    let blob: Blob;
                    let filename: string;

                    if (this.exportType === EXPORT_TYPE.CSV) {
                        blob = new Blob([new Uint8Array(res.data.data)], {
                            type: 'text/csv;charset=utf-8'
                        });
                        filename = 'qna-pairs.csv';
                    } else {
                        blob = new Blob([new Uint8Array(res.data.data)], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
                        });
                        filename = 'qna-pairs.xlsx';
                    }
                    saveAs(blob, filename);
                    this.toast.success("QnA pairs downloaded successfully");
                    this.clearSelections();
                }
                this.isDownloading = false;
                this.showDownloadDialog = false;
            },
            error: (error) => {
                this.toast.error("Failed to download QnA pairs");
                this.isDownloading = false;
                this.clearSelections();
            }
        });
    }

    selectQnaPair(index: number) {
        this.currentEditIndex = index;
        this.scrollToActiveCard();
    }

    navigatePrevious() {
        if (this.currentEditIndex > 0) {
            this.currentEditIndex--;
        }
        this.isPreviousDisabled = this.currentEditIndex === 0;
        this.isNextDisabled = this.currentEditIndex === this.editingPairs.length - 1;
        this.scrollToActiveCard();
    }

    navigateNext() {
        if (this.currentEditIndex < this.editingPairs.length - 1) {
            this.currentEditIndex++;
        }
        this.isPreviousDisabled = this.currentEditIndex === 0;
        this.isNextDisabled = this.currentEditIndex === this.editingPairs.length - 1;
        this.scrollToActiveCard();
    }

    scrollToActiveCard() {
        afterNextRender(() => {
            const cards = this.qnaCards.toArray();
            cards[this.currentEditIndex]?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, { injector: this.injector });
    }

    onScrollEditDialog() {
        this.skip += this.editDialogLimit;
        this.getQnAPairs(3);
    }


    onCancelBulkEdit() {
        this.showBulkEditDialog = false;
        this.editingPairs = [];
        this.currentEditIndex = 0;
        this.resetEditDialog();
        this.isPreviousDisabled = true;
        this.clearSelections();
    }


    onSaveBulkEdit(checkComplete: boolean = true) {
        // Only include pairs that have been saved 
        const modifiedPairs = Array.from(this.savedPairs).map(index => {
            const pair = this.editingPairs[index];
            return {
                id: pair._id,
                question: pair.question || '',
                answer: pair.answer || '',
                reviewer: pair.reviewer || ''
            };
        });
        if (checkComplete && modifiedPairs.length !== this.editingPairs.length) {
            this.saveIncompletePairs();
            return;
        }
        this.isSaving = true;
        this.qnaPairService.bulkUpdateQnaPairs(modifiedPairs).subscribe({
            next: (res) => {
                this.isSaving = false;
                if (res.success) {
                    this.toast.success(`${modifiedPairs.length} QnA pairs updated successfully`);
                    this.showBulkEditDialog = false;
                    this.skip = 0;
                    this.getQnAPairs();
                    this.onCancelBulkEdit();
                }
            },
            error: (error) => {
                this.isSaving = false;
                this.toast.error("Failed to update QnA pairs");
                this.clearSelections();
            }
        });
    }

    saveIncompletePairs() {
        this.dialogService.confirm({
            title: 'Save Incomplete Q&A Pair?',
            description: 'You can save progress, but incomplete data may affect performance. Continue?',
            confirmText: 'Yes, Continue',
            cancelText: 'Complete Q&A First',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.onSaveBulkEdit(false);
            }
        });
    }

    resetEditDialog() {
        this.totalEditingCount = 0;
        this.isPreviousDisabled = true;
        this.isNextDisabled = true;
        this.isSaveDisabled = true;
        this.savedPairs.clear();
    }

    getAdmins() {
        this.isLoadingUsers = true;
        this.userService.getWorkspaceUsers({ includeAdmins: true }).subscribe(res => {
            if (res && res.success) {
                this.admins = res.data;
                this.userOptions = res.data.map(user => ({
                    label: user.fullName || `${user.firstName} ${user.lastName}` || user.email,
                    value: user._id
                }));
            }
            this.isLoadingUsers = false;
        });
    }

    savePair(index: number) {
        if (this.originalPairs[index].question === this.editingPairs[index].question && this.originalPairs[index].answer === this.editingPairs[index].answer && this.originalPairs[index].reviewer === this.editingPairs[index].reviewer) {
            this.savedPairs.delete(index);
        } else {
            this.savedPairs.add(index);
        }
        this.isSaveDisabled = this.savedPairs.size === 0;
    }


    onSort(event: any) {
        // Handle frontend sorting - p-table will handle the sorting automatically
        // No need to make API calls for sorting
    }

    sortQnaPairs(field: string, order: SORT_ORDER) {
        if (order === SORT_ORDER.NONE) {
            // Reset to original order
            this.getQnAPairs();
            return;
        }

        this.qnaData.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (field) {
                case 'question':
                    aValue = this.getQuestionTitle(a).toLowerCase();
                    bValue = this.getQuestionTitle(b).toLowerCase();
                    break;
                case 'timesUsed':
                    aValue = a.timesUsed || 0;
                    bValue = b.timesUsed || 0;
                    break;
                case 'lastEdited':
                    aValue = this.getLastEdited(a);
                    bValue = this.getLastEdited(b);
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return order === SORT_ORDER.ASC ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === SORT_ORDER.ASC ? 1 : -1;
            }
            return 0;
        });
    }

    getQuestionTitle(qna: IQnaPair): string {
        if (typeof qna.question === 'string') {
            return qna.question;
        }
        return (qna.question as IFormQuestion)?.title || '';
    }

    getAnswerValue(qna: IQnaPair): string {
        if (typeof qna.answer === 'string') {
            return qna.answer;
        }
        return (qna.answer as IFormAnswer)?.value || '';
    }

    getAuthorName(qna: IQnaPair & { reviewers: Partial<IUser>[] }): string {
        if (typeof qna.author === 'string') {
            return qna.author;
        }
        return (qna.author as IUser)?.fullName || '';
    }

    getReviewerName(qna: IQnaPair): string {
        if ((qna.question as IFormQuestion)?.reviewedBy) {
            const reviewedBy = (qna.question as IFormQuestion).reviewedBy;
            if (typeof reviewedBy === 'string') {
                return reviewedBy;
            }
            // Handle object with _id, fullName, deactivationStatus structure
            if (typeof reviewedBy === 'object' && reviewedBy !== null) {
                if ('fullName' in reviewedBy) {
                    return (reviewedBy as any).fullName || '-';
                }
                // Fallback to IUser structure
                return (reviewedBy as IUser)?.fullName || '-';
            }
        }
        return '-'
    }

    isReviewerDeactivated(qna: IQnaPair): boolean {
        const reviewedBy = (qna.question as IFormQuestion)?.reviewedBy;
        if (!reviewedBy || typeof reviewedBy === 'string') {
            return false;
        }
        if (typeof reviewedBy === 'object' && reviewedBy !== null) {
            if ('deactivationStatus' in reviewedBy) {
                return (reviewedBy as any).deactivationStatus === DEACTIVATION_STATUS.DEACTIVATED;
            }
            const reviewer = reviewedBy as IUser;
            return reviewer.deactivationStatus === DEACTIVATION_STATUS.DEACTIVATED;
        }
        return false;
    }

    getDocumentName(qna: IQnaPair): string {
        if (typeof qna.document === 'string') {
            return qna.document;
        }
        const contractName = (qna.document as any)?.name;
        if (contractName) return contractName;

        const metadata: any = typeof qna.metadata === 'string' ? null : qna.metadata;
        return (
            metadata?.attachment?.filename ||
            metadata?.upload?.attachment?.filename ||
            '-'
        );
    }

    getSourceType(qna: IQnaPair): string {
        if (!qna.sourceType) return '-';
        return qna.sourceType.charAt(0).toUpperCase() + qna.sourceType.slice(1).toLowerCase();
    }

    getLastEdited(qna: IQnaPair): string {
        if ((qna as any).updatedAt) {
            const date = new Date((qna as any).updatedAt);
            // Return ISO string for proper sorting, but format for display
            return date.toISOString();
        }
        return '1970-01-01T00:00:00.000Z'; // Very old date for sorting purposes
    }

    getLastEditedDisplay(qna: IQnaPair): string {
        if ((qna as any).updatedAt) {
            const date = new Date((qna as any).updatedAt);
            return date.toLocaleDateString();
        }
        return '-';
    }

    // Upload dialog methods
    importQna() {
        this.showUploadDialog = true;
        this.selectedFiles = [];
    }

    closeUploadDialog() {
        this.showUploadDialog = false;
        this.selectedFiles = [];
        this.isProcessingUpload = false;
    }

    onFileSelect(event: File | File[]) {
        const files = Array.isArray(event) ? event : (event ? [event] : []);

        // If empty array is received, clear all selected files
        if (files.length === 0) {
            this.selectedFiles = [];
            return;
        }

        // If files are provided, replace the selectedFiles to match file-drop's current state
        // This handles the case when file-drop removes all files
        this.selectedFiles = [...files];
    }

    handleFiles(files: File[]) {
        if (Array.isArray(files)) {
            this.selectedFiles = [...this.selectedFiles, ...files];
        } else {
            this.selectedFiles = [...this.selectedFiles, files];
        }
    }

    removeFile(fileToRemove: File) {
        this.selectedFiles = this.selectedFiles.filter(file => file !== fileToRemove);
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1000;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    processUploadedFiles() {
        this.isProcessingUpload = true;
        this.qnaPairService.extractQnaFromFiles(this.selectedFiles, this.documentTypeId).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success("QnA pairs extraction started");
                    this.closeUploadDialog();
                    this.getQnAPairs();
                }
                this.isProcessingUpload = false;
            },
            error: (error) => {
                this.toast.error("Failed to process uploaded files");
                this.isProcessingUpload = false;
            }
        });
    }

    getUploadedFilesAudit(open: boolean = false) {
        if (!this.uploadedFilesAudit[this.documentTypeId]) {
            const payload = {
                documentType: this.documentTypeId,
                limit: 10,
                skip: this.uploadedFilesAudit[this.documentTypeId]?.length || 0
            }
            this.qnaPairService.getUploadedFilesAudit(payload).subscribe((res) => {
                if (res.success) {
                    this.uploadedFilesAudit[this.documentTypeId] = res.data
                    this.openFilesAuditSidebar = open;
                }
            });
        }
        else {
            this.openFilesAuditSidebar = open;
        }
    }

    openFileInNewTab(file: IQnaPairMetadata) {
        window.open(file.upload.attachment?.url, '_blank');
    }

    openDocumentPreview(file: IQnaPairMetadata) {
        const attachment = file.upload.attachment as IAttachment;
        this.selectedQnaDocumentFile = {
            ...attachment,
            fullUrl: attachment.fullUrl || attachment.url,
        };
        this.selectedQnaDocumentId = file._id as string;
        this.showQnaDocumentPreview = true;
    }

    closeQnaDocumentPreview() {
        this.showQnaDocumentPreview = false;
        this.selectedQnaDocumentFile = null;
        this.selectedQnaDocumentId = null;
    }

    /**
     * Resume QnA-pair extraction for an uploaded file after credits are topped
     * up (Case 3).  Removes the per-file exhaustion record, refreshes the credits
     * signal so the card hides, and re-triggers extraction using the same
     * QnaPairMetadata record without creating a duplicate.
     */
    onResumeQnaExtraction(file: IQnaPairMetadata): void {
        const fileId = file._id as string;
        this.userCreditsService.removeExhaustion('QUESTION_EXTRACTION', fileId).subscribe({
            next: () => {
                this.creditsState.refreshCredits().subscribe();
                this.qnaPairService.retryQnaPairExtraction(fileId).subscribe({
                    next: () => {
                        this.toast.success('QnA extraction resumed');
                        // Force-refresh the audit list so the status chip updates.
                        delete this.uploadedFilesAudit[this.documentTypeId];
                        this.getUploadedFilesAudit(true);
                    },
                    error: () => this.toast.error('Failed to resume QnA extraction'),
                });
            },
            error: () => this.toast.error('Failed to remove exhaustion record'),
        });
    }

    openReviewQnA() {
        this.router.navigate(['settings/innform/qa-pair/review-tabs'], {
            queryParams: { documentTypeId: this.documentTypeId }
        });
    }


    isChildRouteActive(): boolean {
        return this.router.url.includes('review-tabs');
    }
}

